import { useTranslation } from 'react-i18next';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { INDICATOR_DOMAINS } from '@/api/rest/resources/types/IndicatorProduct';
import { CSV_REPORT_TYPES } from '@/api/rest/resources/types/reports';
import { DefaultWidgetNames, GraphTileType, WidgetType } from '@/api/rest/resources/types/widget';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useInSituIndicatorConfigurationOrgWidgetsByDomain } from '@/hooks/useInSituIndicatorConfigurationWidgets';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { CsvExportButton } from '@/pages/shared/reports/components/CsvExportButton';

import { useDefaultCustomCells, useOrderedCustomCells, useWaterCells } from '../plots/hooks/useGetCustomCells';
import { ProjectTypeAccessorKey } from '../projects/pages/project/pages/sustainableProduction/components/plot-list/constants';
import { LandPageLayout } from './components/LandPageLayout';

const PAGE_WIDGET_NAME = 'default_template_asset_water';
const PAGE_WIDGET_TYPE = WidgetType.t_page;

export const LandWaterPage = () => {
  const { t } = useTranslation();

  const defaultWidgetConfiguration = useDefaultWidgetConfiguration();
  const customWidgetConfiguration = useActiveWidgetSlotConfiguration(
    PAGE_WIDGET_NAME,
    PAGE_WIDGET_TYPE,
    undefined,
    defaultWidgetConfiguration,
  );
  const widgetConfiguration = customWidgetConfiguration ?? defaultWidgetConfiguration;

  const defaultCells = [...useDefaultCustomCells(), ...useWaterCells()];
  const orderedCustomCells = useOrderedCustomCells(defaultCells, [
    ProjectTypeAccessorKey.waterHoldingCapacityTrend_1y,
    ProjectTypeAccessorKey.waterHoldingCapacity,
  ]);

  return (
    <LandPageLayout
      signedInPageLayoutProps={{
        customizableWidgetTemplateProps: {
          widgetName: PAGE_WIDGET_NAME,
          widgetType: PAGE_WIDGET_TYPE,
          overrideLayoutConfiguration: defaultWidgetConfiguration,
        },
      }}
      navBarProps={{
        title: t('navbar.water'),
        children: <CsvExportButton reportType={CSV_REPORT_TYPES.WATER_CSV} className='w-fit font-normal' />,
      }}
      plotListProps={{
        customColumns: orderedCustomCells,
      }}
    >
      <WidgetSlotRenderer nestedWidget={widgetConfiguration} />
    </LandPageLayout>
  );
};

const useDefaultWidgetConfiguration = () => {
  const { t } = useTranslation();
  const inSituWidgets = useInSituIndicatorConfigurationOrgWidgetsByDomain(INDICATOR_DOMAINS.WATER);

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.water.labels.waterHoldingCapacity'),
    headerTooltip: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacity.body'),
    showToggle: true,
    showZoom: true,
    showGraphType: true,
  };

  return {
    name: PAGE_WIDGET_NAME,
    type: PAGE_WIDGET_TYPE,
    widgets: [
      {
        name: 'water_asset_summary_row',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'row',
          center: true,
          spacing: 6,
          className: 'flex-col lg:flex-row lg:flex-wrap items-stretch',
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_fact_card_current_whc,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.water_fc_per_ha,
              headerText: 'global.widget.currentWhc',
              tooltipText: 'global.widget.currentWhcTootip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_historic_water_5y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.water_fc_per_ha_previous_5y_pct_change,
              headerText: 'global.widget.historic5yAvgWaterUplift',
              tooltipText: 'global.widget.historic5yUpliftTooltip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_projection_water_1y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: IndicatorName.water_fc_previous_1y_uplift_per_ha_pct_change,
              headerText: 'global.widget.projection1yWaterUplift',
              tooltipText: 'global.widget.projection1yUpliftTooltip',
            },
            visible: true,
          },
        ],
      },
      {
        name: 'asset_whc_indicator_card',
        type: WidgetType.o_details_graph_card,
        visible: true,
        relatedData: {
          headerProps: indicatorHeaderProps,
          graphTileType: GraphTileType.historic_water_soil_moisture,
        },
      },
      ...inSituWidgets,
    ],
  };
};
